import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "./villas-for-sale-abu-dhabi.css";

const furnishedIncludes = [
  { icon: "fas fa-bed", title: "Comfortable bed and seating area." },
  { icon: "fas fa-utensils", title: "Kitchen tools and appliances." },
  { icon: "fas fa-wifi", title: "High-speed internet access." },
  { icon: "fas fa-plug", title: "Water and electricity included." },
  { icon: "fas fa-broom", title: "Regular maintenance services." },
  { icon: "fas fa-dumbbell", title: "Access to gyms and pools." },
];

const bestAreas = [
  { icon: "fas fa-water", title: "Al Reem Island", text: "Modern towers with amazing views of the water." },
  { icon: "fas fa-ticket-alt", title: "Yas Island", text: "Full of energy and near famous theme parks." },
  { icon: "fas fa-landmark", title: "Saadiyat Island", text: "A quiet place near the beach and museums." },
  { icon: "fas fa-road", title: "Al Raha Beach", text: "Luxury living with easy access to the highway." },
];

const rentalIncludes = [
  { icon: "fas fa-fan", title: "Central air conditioning." },
  { icon: "fas fa-box-archive", title: "Built-in wardrobes for storage." },
  { icon: "fas fa-broom", title: "Professional housekeeping options." },
  { icon: "fas fa-square-parking", title: "Secure parking spaces." },
  { icon: "fas fa-shield-halved", title: "Building security and concierge." },
  { icon: "fas fa-shirt", title: "Laundry facilities." },
];

const requiredDocuments = [
  { icon: "fas fa-passport", title: "A copy of your passport." },
  { icon: "fas fa-id-card", title: "A valid residency visa." },
  { icon: "fas fa-id-badge", title: "Your original Emirates ID." },
  { icon: "fas fa-file-invoice-dollar", title: "A certificate showing your salary." },
  { icon: "fas fa-money-check", title: "Personal bank checks for payment." },
];

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Furnished Studio for Rent in Abu Dhabi", item: "https://nvrealestate.ae/furnished-studio-rent-abu-dhabi" },
  ],
};

function FurnishedStudioRentAbuDhabi() {
  return (
    <>
      <Helmet>
        <title>Furnished Studio for Rent in Abu Dhabi – Ready Homes</title>
        <meta
          name="description"
          content="Searching for a furnished studio for rent in Abu Dhabi? NV Real Estate team is fully prepared to help you get the best property matching your requirements and expectations."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/furnished-studio-rent-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi rental specialists</span>
              <h1 id="villa-page-title">Furnished Studio for Rent in Abu Dhabi – Prime Locations</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Rent&city=&developer=&developer_name=&project=&project_name=&property_type=Studio&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore studios
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/furnished-studio-rent-abu-dhabi/02.png"
                alt="Furnished studios for rent in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Move-in ready</strong>
                <span>Just bring your suitcase</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Finding Your Perfect Furnished Studio for Rent in Abu Dhabi with Nova Vision Real Estate</h2>
              <p>
                Living in the capital of the UAE is an exciting dream for many professionals.
                Finding a furnished studio for rent in Abu Dhabi is the first step to your new life.
              </p>
              <p>
                NV Real Estate is here to make your property search smooth and successful. We are
                experts who understand the unique Abu Dhabi housing market.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-furnished-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Furnished living</span>
              <h2 id="why-furnished-heading">Why Choose a Furnished Home?</h2>
              <p>
                A furnished studio for rent in Abu Dhabi is the perfect choice for busy newcomers. It
                saves you the time and trouble of buying new furniture.
              </p>
              <p>
                Our studios are ready for you to move in with just your suitcase. They include a
                bed, a sofa, and all the tools you need for your kitchen.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {furnishedIncludes.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="expert-guidance-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Expert guidance</span>
              <h2 id="expert-guidance-heading">Expert Guidance from Nova Vision</h2>
              <p>
                Our team has over 20 years of experience helping people find homes in the UAE. We
                use our deep knowledge to find the best spots for your lifestyle.
              </p>
              <p>
                We pride ourselves on being a trusted partner for all our clients. We offer
                professional advice to ensure your rental contract is safe and legal.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="best-areas-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Where to live</span>
              <h2 id="best-areas-heading">The Best Areas to Explore</h2>
              <p>
                Abu Dhabi has many beautiful neighborhoods with different vibes. We help you choose
                the one that fits your work and hobbies.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {bestAreas.map((area, index) => (
                <article className="villa-benefit-card" key={area.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={area.icon} /></span>
                  <h3>{area.title}</h3>
                  <p>{area.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="flexibility-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Your options</span>
              <h2 id="flexibility-heading">Flexibility for Your Lifestyle</h2>
              <p>
                If you want a furnished studio for rent in Abu Dhabi, you have flexible choices. You
                can pick a monthly stay or a yearly contract.
              </p>
              <p>
                Monthly stays are great if you are here for a short project. You can leave easily
                with a short notice period.
              </p>
              <p>
                Yearly contracts offer more stability for long-term residents. These agreements are
                registered with the government for your protection.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="whats-included-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">All-inclusive</span>
              <h2 id="whats-included-heading">What is Included in Your Rental?</h2>
              <p>
                Most furnished studios come as all-inclusive packages. This means your bills are
                bundled into one simple payment.
              </p>
              <p>
                You do not have to worry about setting up accounts for power or water. Everything is
                ready for you from the very first day.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {rentalIncludes.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="how-we-help-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Full support</span>
              <h2 id="how-we-help-heading">How NV Real Estate Helps You</h2>
              <p>
                We are a local team available seven days a week to help you. We listen to what you
                need and find a list of homes just for you.
              </p>
              <p>
                Our experts handle the hard parts of the rental process. We arrange your viewings
                and help you understand all the paperwork.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="get-started-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Getting started</span>
              <h2 id="get-started-heading">What You Need to Get Started</h2>
              <p>
                To rent your new home, you will need a few simple documents. Having these ready
                makes the process very fast and easy.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {requiredDocuments.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card villa-advisor__card--single">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Trust NV Real Estate for Your Move</h2>
                <p>
                  We are a licensed and professional real estate company in Abu Dhabi. Our goal is
                  to provide a stress-free experience for every tenant.
                </p>
                <p className="villa-advisor__closing">
                  Let us help you find the best furnished studio for rent in Abu Dhabi today.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also send
                  us an email at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
        .villa-advisor__card--single {
          display: block;
        }
      `}</style>
    </>
  );
}

export default FurnishedStudioRentAbuDhabi;